import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  Truck,
  Users,
  FileText,
  AlertTriangle,
  CheckCircle,
  Search,
  Shield,
  Target,
  Zap,
  BarChart3,
  Database,
  Activity
} from 'lucide-react';
import { Navbar } from '../components/shared/Navbar';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { logger } from '../utils/logger';
import { statsApi } from '../services/api';

interface PlatformStats {
  carriers: {
    total: number;
    active: number;
    inactive: number;
    activeCarrierAuthority: number;
    activeBrokerAuthority: number;
    dualAuthority: number;
    revokedCarrierAuthority: number;
    revokedBrokerAuthority: number;
    insuranceExpiring30d?: number;
    brokerBondExpiring30d?: number;
    totalDualAuthority?: number;
    dualAuthorityRevoked?: number;
  };
  vins: {
    uniqueFromCrashes: number;
    uniqueFromInspections: number;
    estimatedTotal: number;
    new30d?: number;
  };
  inspections: {
    total: number;
    performed30d?: number;
  };
  crashes: {
    total: number;
    reported30d?: number;
  };
}

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [carrierBreakdown, setCarrierBreakdown] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [overviewData, breakdownData] = await Promise.all([
          statsApi.getOverview(),
          statsApi.getCarrierBreakdown(),
        ]);
        setStats(overviewData as unknown as PlatformStats);
        setCarrierBreakdown(breakdownData);
      } catch (error) {
        logger.error('Failed to fetch platform stats', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatNumber = (num: number | undefined): string => {
    if (num === undefined) return 'N/A';
    return num.toLocaleString();
  };

  const navigateToCarrierList = (classification: string) => {
    navigate(`/market-intelligence?carrierClassification=${classification}`);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-gray-50)' }}>
      <Navbar />

      <div className="container-spacious" style={{ paddingTop: 'var(--space-3xl)', paddingBottom: 'var(--space-3xl)' }}>

        {/* Hero Section — hairline, serif, ink on paper */}
        <div className="fade-in-up" style={{
          animationDelay: '0.1s',
          textAlign: 'center',
          marginBottom: 'var(--space-2xl)',
          padding: 'var(--space-3xl) var(--space-lg)',
          borderBottom: '1px solid var(--color-gray-200)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'var(--space-lg)' }}>
            <Shield size={40} style={{ marginRight: 'var(--space-md)' }} />
            <h1 style={{ margin: 0, fontSize: '2.25rem' }}>
              Transportation Safety & Analytics Platform
            </h1>
          </div>
          <p style={{ fontSize: '1.125rem', color: 'var(--color-gray-600)', maxWidth: '720px', margin: '0 auto' }}>
            Comprehensive carrier intelligence powered by 28.5M+ government records
          </p>
          <button
            onClick={() => navigate('/carrier-search')}
            className="btn-primary"
            style={{
              marginTop: 'var(--space-xl)',
              padding: '1rem 2rem',
              fontSize: '1.125rem'
            }}
          >
            <Search size={20} style={{ marginRight: 'var(--space-sm)' }} />
            Start Carrier Search
          </button>
        </div>

        {/* Platform Statistics */}
        <div className="fade-in-up" style={{ animationDelay: '0.2s', marginBottom: 'var(--space-2xl)' }}>
          <h2 className="heading-2" style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
            <Database size={28} style={{ verticalAlign: 'middle', marginRight: 'var(--space-sm)' }} />
            Platform Database Statistics
          </h2>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
              <LoadingSpinner />
            </div>
          ) : stats ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 'var(--space-lg)',
              marginBottom: 'var(--space-2xl)'
            }}>
              {/* Total Carriers */}
              <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--color-gray-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto var(--space-md)'
                }}>
                  <Truck size={32} color="#4a463f" />
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-gray-900)', marginBottom: 'var(--space-xs)' }}>
                  {formatNumber(stats.carriers.total)}
                </div>
                <div style={{ color: 'var(--color-gray-600)', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                  Total Carriers
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                  <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>{formatNumber(stats.carriers.active)}</span> Active
                  {' • '}
                  <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>{formatNumber(stats.carriers.revokedCarrierAuthority)}</span> Revoked
                </div>
                {stats.carriers.insuranceExpiring30d !== undefined && stats.carriers.insuranceExpiring30d > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginTop: 'var(--space-xs)', fontWeight: 600 }}>
                    ⚠️ {formatNumber(stats.carriers.insuranceExpiring30d)} insurance expiring in 30 days
                  </div>
                )}
              </div>

              {/* Total Brokers */}
              <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#efe3c8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto var(--space-md)'
                }}>
                  <Users size={32} color="#b07d2b" />
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-warning)', marginBottom: 'var(--space-xs)' }}>
                  {formatNumber(stats.carriers.activeBrokerAuthority)}
                </div>
                <div style={{ color: 'var(--color-gray-600)', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                  Active Brokers
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                  <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>{formatNumber(stats.carriers.activeBrokerAuthority)}</span> Active
                  {' • '}
                  <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>{formatNumber(stats.carriers.revokedBrokerAuthority)}</span> Revoked
                </div>
                {stats.carriers.brokerBondExpiring30d !== undefined && stats.carriers.brokerBondExpiring30d > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-warning)', marginTop: 'var(--space-xs)', fontWeight: 600 }}>
                    ⚠️ {formatNumber(stats.carriers.brokerBondExpiring30d)} bonds expiring in 30 days
                  </div>
                )}
              </div>

              {/* Dual Authority */}
              <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--color-gray-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto var(--space-md)'
                }}>
                  <TrendingUp size={32} color="#4a463f" />
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-gray-900)', marginBottom: 'var(--space-xs)' }}>
                  {formatNumber(stats.carriers.totalDualAuthority || stats.carriers.dualAuthority)}
                </div>
                <div style={{ color: 'var(--color-gray-600)', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                  Dual Authority
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                  Carrier + Broker combined
                </div>
                {stats.carriers.dualAuthorityRevoked !== undefined && stats.carriers.dualAuthorityRevoked > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-danger)', marginTop: 'var(--space-xs)', fontWeight: 600 }}>
                    {formatNumber(stats.carriers.dualAuthorityRevoked)} with revoked authority
                  </div>
                )}
              </div>

              {/* Total VINs */}
              <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#e2e7d5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto var(--space-md)'
                }}>
                  <FileText size={32} color="#5d7148" />
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-success)', marginBottom: 'var(--space-xs)' }}>
                  {formatNumber(stats.vins.estimatedTotal)}
                </div>
                <div style={{ color: 'var(--color-gray-600)', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                  Unique VINs
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                  Vehicle identification numbers tracked
                </div>
                {stats.vins.new30d !== undefined && stats.vins.new30d > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-success)', marginTop: 'var(--space-xs)', fontWeight: 600 }}>
                    +{formatNumber(stats.vins.new30d)} seen in last 30 days
                  </div>
                )}
              </div>

              {/* Total Inspections */}
              <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: 'var(--color-gray-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto var(--space-md)'
                }}>
                  <CheckCircle size={32} color="#6d685e" />
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-gray-700)', marginBottom: 'var(--space-xs)' }}>
                  {formatNumber(stats.inspections.total)}
                </div>
                <div style={{ color: 'var(--color-gray-600)', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                  Safety Inspections
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                  Complete inspection records
                </div>
                {stats.inspections.performed30d !== undefined && stats.inspections.performed30d > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', marginTop: 'var(--space-xs)', fontWeight: 600 }}>
                    {formatNumber(stats.inspections.performed30d)} in last 30 days
                  </div>
                )}
              </div>

              {/* Total Crashes */}
              <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: '#f1e2da',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto var(--space-md)'
                }}>
                  <AlertTriangle size={32} color="#b3412c" />
                </div>
                <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--color-danger)', marginBottom: 'var(--space-xs)' }}>
                  {formatNumber(stats.crashes.total)}
                </div>
                <div style={{ color: 'var(--color-gray-600)', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                  Crash Records
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--color-gray-500)' }}>
                  Accident reports and outcomes
                </div>
                {stats.crashes.reported30d !== undefined && stats.crashes.reported30d > 0 && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-danger)', marginTop: 'var(--space-xs)', fontWeight: 600 }}>
                    {formatNumber(stats.crashes.reported30d)} in last 30 days
                  </div>
                )}
              </div>

            </div>
          ) : (
            <div className="card-modern" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
              <p style={{ color: 'var(--color-gray-500)' }}>Unable to load statistics. Please try again later.</p>
            </div>
          )}
        </div>

        {/* Carrier Breakdown */}
        {carrierBreakdown && (
          <div className="card-modern fade-in-up" style={{ animationDelay: '0.25s', marginBottom: 'var(--space-2xl)' }}>
            <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', textAlign: 'center' }}>
              <Users size={28} style={{ verticalAlign: 'middle', marginRight: 'var(--space-sm)' }} />
              Carrier Classification Breakdown
            </h2>

            {/* Authority Status with Operation Scope Cross-Classification */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 'var(--space-md)', marginBottom: 'var(--space-xl)' }}>
              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-primary)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('for_hire_interstate')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>{carrierBreakdown.byAuthority?.forHire?.interstate?.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🌎 For-Hire Interstate</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-gray-600)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('for_hire_intrastate_non_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-600)' }}>{carrierBreakdown.byAuthority?.forHire?.intrastateNonHazmat?.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🏙️ For-Hire Intrastate</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-gray-700)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('for_hire_intrastate_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-700)' }}>{carrierBreakdown.byAuthority?.forHire?.intrastateHazmat?.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>☢️ For-Hire Hazmat</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-success)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('private_interstate')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-success)' }}>{carrierBreakdown.byAuthority?.private?.interstate?.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🌎 Private Interstate</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-warning)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('private_intrastate_non_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)' }}>{carrierBreakdown.byAuthority?.private?.intrastateNonHazmat?.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🏙️ Private Intrastate</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-danger)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('private_intrastate_hazmat')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-danger)' }}>{carrierBreakdown.byAuthority?.private?.intrastateHazmat?.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>☢️ Private Hazmat</div>
              </div>
            </div>

            {/* Insurance Cancellations Alert */}
            {carrierBreakdown.insuranceCancellations && carrierBreakdown.insuranceCancellations.totalCritical30Days > 0 && (
              <div style={{ marginBottom: 'var(--space-xl)', padding: 'var(--space-md)', background: '#f1e2da', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-danger)' }}>
                <div style={{ textAlign: 'center', marginBottom: 'var(--space-md)', color: 'var(--color-danger)', fontWeight: 600, fontSize: '1.1rem' }}>
                  Critical: Insurance Cancellations
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 'var(--space-sm)' }}>
                  <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-danger)', textAlign: 'center', cursor: 'pointer', background: '#fbf9f4' }} onClick={() => navigateToCarrierList('insurance_cancelling_30days')}>
                    <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--color-danger)' }}>{carrierBreakdown.insuranceCancellations.totalCritical30Days?.toLocaleString()}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🚨 Cancelling in 30d</div>
                  </div>
                  <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-warning)', textAlign: 'center', cursor: 'pointer', background: '#fbf9f4' }} onClick={() => navigateToCarrierList('insurance_pending_cancellation')}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)' }}>{carrierBreakdown.insuranceCancellations.totalPendingCancellation?.toLocaleString()}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>⚠️ Total Pending</div>
                  </div>
                </div>
              </div>
            )}

            {/* For-Hire Carrier Types */}
            <h3 className="heading-3" style={{ marginBottom: 'var(--space-md)', textAlign: 'center' }}>For-Hire Carrier Types</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 'var(--space-md)' }}>
              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-primary)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('property_carriers')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-primary)' }}>{carrierBreakdown.forHireBreakdown?.propertyCarriers?.total.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🚛 Property</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-success)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('bus_companies')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-success)' }}>{carrierBreakdown.forHireBreakdown?.busCompanies?.total.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🚌 Buses</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-warning)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('moving_companies')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-warning)' }}>{carrierBreakdown.forHireBreakdown?.movingCompanies?.total.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>🏠 Movers</div>
              </div>

              <div className="card-modern" style={{ padding: 'var(--space-md)', borderLeft: '4px solid var(--color-gray-700)', textAlign: 'center', cursor: 'pointer', transition: 'transform 0.2s' }} onClick={() => navigateToCarrierList('brokers')} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--color-gray-700)' }}>{carrierBreakdown.forHireBreakdown?.brokers?.total.toLocaleString()}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginTop: '6px' }}>📦 Brokers</div>
              </div>
            </div>
          </div>
        )}

        {/* Why Use This Platform */}
        <div className="card-modern fade-in-up" style={{ animationDelay: '0.3s', marginBottom: 'var(--space-2xl)' }}>
          <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)', textAlign: 'center' }}>
            <Target size={28} style={{ verticalAlign: 'middle', marginRight: 'var(--space-sm)' }} />
            Why Use This Platform?
          </h2>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 'var(--space-xl)' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'start', marginBottom: 'var(--space-md)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'var(--color-gray-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 'var(--space-md)',
                  flexShrink: 0
                }}>
                  <Shield size={24} color="#4a463f" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                    Comprehensive Safety Intelligence
                  </h3>
                  <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                    Access the complete safety history of any carrier including inspections, violations, crashes, and BASIC scores. Make informed decisions based on real FMCSA data.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'start', marginBottom: 'var(--space-md)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#efe3c8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 'var(--space-md)',
                  flexShrink: 0
                }}>
                  <Zap size={24} color="#b07d2b" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                    Shell Game Detection
                  </h3>
                  <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                    Identify carriers attempting to hide their safety record by starting new operations. Our VIN tracking system catches shell game tactics.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'start', marginBottom: 'var(--space-md)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#e2e7d5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 'var(--space-md)',
                  flexShrink: 0
                }}>
                  <BarChart3 size={24} color="#5d7148" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                    Predictive Analytics
                  </h3>
                  <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                    Our BASIC score prediction system forecasts future safety ratings and identifies high-risk carriers before they become a liability.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'start', marginBottom: 'var(--space-md)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'var(--color-gray-100)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 'var(--space-md)',
                  flexShrink: 0
                }}>
                  <TrendingUp size={24} color="#6d685e" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                    Merger Analysis
                  </h3>
                  <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                    Evaluate the combined safety impact of carrier acquisitions and mergers with FMCSA-compliant calculations and risk assessments.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'start', marginBottom: 'var(--space-md)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#f1e2da',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 'var(--space-md)',
                  flexShrink: 0
                }}>
                  <AlertTriangle size={24} color="#b3412c" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                    Real-Time Monitoring
                  </h3>
                  <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                    Monitor carriers on your watchlist and receive alerts when their safety status changes or new violations are reported.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'start', marginBottom: 'var(--space-md)' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: '#f2dad2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 'var(--space-md)',
                  flexShrink: 0
                }}>
                  <FileText size={24} color="#b3412c" />
                </div>
                <div>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                    Comprehensive Reports
                  </h3>
                  <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
                    Generate detailed PDF reports for due diligence, compliance reviews, and risk assessments. Export data in CSV and JSON formats.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tips & Tricks */}
        <div className="card-modern fade-in-up" style={{ animationDelay: '0.4s' }}>
          <h2 className="heading-2" style={{ marginBottom: 'var(--space-xl)' }}>
            💡 Tips & Tricks
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-lg)',
              borderLeft: '4px solid var(--color-gray-700)'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                🔍 Search Like a Pro
              </h3>
              <p style={{ color: 'var(--color-gray-600)', marginBottom: 'var(--space-sm)' }}>
                You can search by <strong>DOT number</strong>, <strong>MC number</strong>, <strong>VIN</strong>, or <strong>company name</strong>.
                The system auto-detects what you're searching for!
              </p>
              <p style={{ color: 'var(--color-gray-500)', fontSize: '0.875rem' }}>
                Examples: <code style={{ background: '#fbf9f4', padding: '2px 8px', borderRadius: '4px' }}>123456</code>,
                <code style={{ background: '#fbf9f4', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px' }}>MC 12345</code>,
                <code style={{ background: '#fbf9f4', padding: '2px 8px', borderRadius: '4px', marginLeft: '8px' }}>ACME TRUCKING</code>
              </p>
            </div>

            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-lg)',
              borderLeft: '4px solid var(--color-success)'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                📊 Understanding BASIC Scores
              </h3>
              <p style={{ color: 'var(--color-gray-600)', marginBottom: 'var(--space-sm)' }}>
                BASIC scores range from 0-100. Lower is better! Scores above 65 indicate high risk in that category.
              </p>
              <ul style={{ color: 'var(--color-gray-600)', marginLeft: 'var(--space-lg)' }}>
                <li><strong>0-50:</strong> Good standing</li>
                <li><strong>51-65:</strong> Caution - monitor closely</li>
                <li><strong>66-85:</strong> High risk - intervention needed</li>
                <li><strong>86-100:</strong> Critical - immediate action required</li>
              </ul>
            </div>

            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-lg)',
              borderLeft: '4px solid var(--color-warning)'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                🎯 Monitor List Feature
              </h3>
              <p style={{ color: 'var(--color-gray-600)' }}>
                Add high-priority carriers to your monitor list for <strong>100% accurate</strong> FMCSA data.
                The system automatically scrapes their official portal page daily to ensure you have the most current information.
              </p>
            </div>

            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-lg)',
              borderLeft: '4px solid var(--color-gray-600)'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                🚨 Shell Game Detection
              </h3>
              <p style={{ color: 'var(--color-gray-600)' }}>
                Look for the <strong>"Shell Game Alert"</strong> indicator on carrier profiles. This flags carriers who may be
                restarting operations with a new DOT number to hide previous violations - a common tactic to avoid consequences.
              </p>
            </div>

            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-lg)',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                🔗 VIN Tracking
              </h3>
              <p style={{ color: 'var(--color-gray-600)' }}>
                Search by any 17-character VIN to see all carriers who have operated that vehicle. This is crucial for
                identifying shell game patterns and verifying fleet ownership history.
              </p>
            </div>

            <div style={{
              padding: 'var(--space-lg)',
              background: 'var(--color-gray-50)',
              borderRadius: 'var(--radius-lg)',
              borderLeft: '4px solid var(--color-danger)'
            }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 'var(--space-sm)' }}>
                📈 Merger Analysis Tool
              </h3>
              <p style={{ color: 'var(--color-gray-600)' }}>
                Evaluating a carrier acquisition? Use the <strong>Merge Carriers</strong> feature to calculate combined BASIC scores
                and see how the merger would impact overall safety ratings. Export professional reports for stakeholders.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="card-modern fade-in-up" style={{
          animationDelay: '0.5s',
          textAlign: 'center',
          padding: 'var(--space-3xl) var(--space-lg)',
          borderTop: '1px solid var(--color-gray-200)',
          marginTop: 'var(--space-2xl)'
        }}>
          <h2 className="heading-2" style={{ marginBottom: 'var(--space-md)' }}>
            Ready to Get Started?
          </h2>
          <p style={{ fontSize: '1.125rem', color: 'var(--color-gray-600)', marginBottom: 'var(--space-xl)', maxWidth: '600px', margin: '0 auto var(--space-xl)' }}>
            Start searching carriers, analyzing safety data, and making informed decisions today.
          </p>
          <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/carrier-search')}
              className="btn-primary"
              style={{
                padding: '1rem 2rem',
                fontSize: '1.125rem'
              }}
            >
              <Search size={20} style={{ marginRight: 'var(--space-sm)' }} />
              Search Carriers
            </button>
            <button
              onClick={() => navigate('/merge')}
              className="btn-secondary"
              style={{
                padding: '1rem 2rem',
                fontSize: '1.125rem'
              }}
            >
              <TrendingUp size={20} style={{ marginRight: 'var(--space-sm)' }} />
              Analyze Mergers
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
